import { useTheme } from "@emotion/react";
import _ from "lodash";
import React from "react";

import { DiffSyncDirection, DiffSyncRecord, ValueFormatter } from "./types";

export const getChangedFields = <T extends Record<string, unknown>>(
  diff: DiffSyncRecord<T>,
  direction: DiffSyncDirection,
) => {
  const source = getDirectionValue(diff, direction);
  const baseline = getBaselineValue(diff, direction);

  if (!source) {
    return baseline ? new Set(Object.keys(baseline) as (keyof T & string)[]) : new Set<keyof T & string>();
  }
  if (!baseline) return new Set(Object.keys(source) as (keyof T & string)[]);

  const keys = Array.from(new Set([...Object.keys(source), ...Object.keys(baseline)]));

  return new Set(keys.filter((key) => !_.isEqual(source[key], baseline[key])) as (keyof T & string)[]);
};

export const isNewRecord = <T extends Record<string, unknown>>(diff: DiffSyncRecord<T>, direction: DiffSyncDirection) =>
  !!getDirectionValue(diff, direction) && !getBaselineValue(diff, direction);

export const isDeletedRecord = <T extends Record<string, unknown>>(
  diff: DiffSyncRecord<T>,
  direction: DiffSyncDirection,
) => !getDirectionValue(diff, direction) && !!getBaselineValue(diff, direction);

export const isFieldAddition = <T extends Record<string, unknown>>(
  source: T | null,
  baseline: T | null,
  key: keyof T & string,
) => {
  const oldValue = baseline && hasOwn(baseline, key) ? baseline[key] : undefined;
  const newValue = source && hasOwn(source, key) ? source[key] : undefined;

  return isEmptyValue(oldValue) && !isEmptyValue(newValue);
};

export const isFieldDeletion = <T extends Record<string, unknown>>(
  source: T | null,
  baseline: T | null,
  key: keyof T & string,
) => {
  const oldValue = baseline && hasOwn(baseline, key) ? baseline[key] : undefined;
  const newValue = source && hasOwn(source, key) ? source[key] : undefined;

  return !isEmptyValue(oldValue) && isEmptyValue(newValue);
};

export const getDirectionValue = <T extends Record<string, unknown>>(
  diff: DiffSyncRecord<T>,
  direction: DiffSyncDirection,
) => (direction === "pull" ? diff.remote : diff.local);

export const getBaselineValue = <T extends Record<string, unknown>>(
  diff: DiffSyncRecord<T>,
  direction: DiffSyncDirection,
) => (direction === "pull" ? diff.local : diff.remote);

export const getCellValue = <T extends Record<string, unknown>>(
  source: T | null,
  baseline: T | null,
  key: keyof T & string,
  isChanged: boolean,
) => {
  if (source && hasOwn(source, key)) return source[key];
  if (isChanged) return undefined;
  if (baseline && hasOwn(baseline, key)) return baseline[key];

  return undefined;
};

export const renderCellValue = <T extends Record<string, unknown>>(
  source: T | null,
  baseline: T | null,
  key: keyof T & string,
  isChanged: boolean,
  isNew: boolean,
  formatter?: ValueFormatter,
) => {
  if (isChanged && !isNew) {
    const oldValue = baseline && hasOwn(baseline, key) ? baseline[key] : undefined;
    const newValue = source && hasOwn(source, key) ? source[key] : undefined;

    if (isEmptyValue(oldValue)) return formatValue(newValue, formatter);
    if (isEmptyValue(newValue)) return <RemovedValue value={oldValue} formatter={formatter} />;

    return <ValueChange oldValue={oldValue} newValue={newValue} formatter={formatter} />;
  }

  return formatValue(getCellValue(source, baseline, key, isChanged), formatter);
};

export const RemovedValue = (props: { value: unknown; formatter?: ValueFormatter }) => {
  const theme = useTheme();

  return (
    <span css={{ color: theme.colors.danger, fontWeight: 400, textDecoration: "line-through" }}>
      {formatValue(props.value, props.formatter)}
    </span>
  );
};

export const formatValue = (value: unknown, formatter?: ValueFormatter): React.ReactNode => {
  if (isEmptyValue(value)) return "-";
  if (formatter) return formatter(value);
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") return value.toString();
  if (Array.isArray(value)) return value.map((item) => formatValue(item)).join(", ");
  if (hasCustomToString(value)) return value.toString();

  return JSON.stringify(value);
};

const ValueChange = (props: { oldValue: unknown; newValue: unknown; formatter?: ValueFormatter }) => (
  <span css={{ display: "inline-flex", alignItems: "center", gap: "0.375rem" }}>
    <span css={{ fontWeight: 400, opacity: 0.75 }}>{formatValue(props.oldValue, props.formatter)}</span>
    <span>→</span>
    <span>{formatValue(props.newValue, props.formatter)}</span>
  </span>
);

const hasOwn = <T extends Record<string, unknown>>(value: T, key: keyof T & string) =>
  Object.prototype.hasOwnProperty.call(value, key);

const hasCustomToString = (value: unknown): value is { toString: () => string } => {
  if (typeof value !== "object" || value === null) return false;

  const toString = (value as { toString?: unknown }).toString;
  return typeof toString === "function" && toString !== Object.prototype.toString;
};

const isEmptyValue = (value: unknown) =>
  value === null || value === undefined || value === "" || (Array.isArray(value) && value.length === 0);
